import { motion } from 'framer-motion';
import { useState } from 'react';

const pricingPlans = [
  {
    name: 'Starter',
    description: 'Perfekt für Einsteiger und Privatpersonen',
    price: '1.299',
    features: [
      'IHoloTool Basisgerät',
      '50 GB Cloud-Speicher',
      'Grundlegende 3D-Modelle',
      'E-Mail-Support',
      '1 Jahr Garantie'
    ],
    popular: false,
    buttonText: 'Vorbestellen',
    buttonVariant: 'outline'
  },
  {
    name: 'Professional',
    description: 'Ideal für Profis und kleine Unternehmen',
    price: '2.499',
    features: [
      'IHoloTool Pro Gerät',
      '500 GB Cloud-Speicher',
      'Erweiterte 3D-Modelle',
      'Priorisierter Support',
      '2 Jahre Garantie',
      'Exklusive Beta-Features',
      'Monatliche Q&A-Sessions'
    ],
    popular: true,
    buttonText: 'Jetzt vorbestellen',
    buttonVariant: 'primary'
  },
  {
    name: 'Enterprise',
    description: 'Maßgeschneiderte Lösung für Unternehmen',
    price: 'Individuell',
    features: [
      'Maßgeschneiderte IHoloTool-Lösung',
      'Unbegrenzter Cloud-Speicher',
      'Premium 3D-Modelle',
      '24/7 VIP-Support',
      '3 Jahre Garantie',
      'Dedizierter Account Manager',
      'Schulungen & Workshops',
      'API-Zugriff'
    ],
    popular: false,
    buttonText: 'Anfrage senden',
    buttonVariant: 'outline'
  }
];

const Preorder = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    plan: 'Professional',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    // Here you would typically send the data to your backend
  };

  // Launch date: December 1, 2025
  const launchDate = new Date('2025-12-01T00:00:00');
  const now = new Date();
  const timeLeft = launchDate.getTime() - now.getTime();
  
  const daysLeft = Math.ceil(timeLeft / (1000 * 60 * 60 * 24));
  const hoursLeft = Math.ceil((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  return (
    <section id="preorder" className="py-20 bg-gray-900 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 -left-20 w-96 h-96 rounded-full bg-purple-600 filter blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 rounded-full bg-blue-600 filter blur-3xl"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-400">
              IHoloTool vorbestellen
            </span>
          </h2>
          <p className="text-lg text-gray-300">
            Sichern Sie sich jetzt Ihr IHoloTool und werden Sie Teil der Holografie-Revolution.
            Die ersten 1.000 Bestellungen erhalten exklusive Vorteile.
          </p>
          
          {/* Countdown */}
          <div className="mt-10 bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 inline-block border border-gray-700/50">
            <div className="text-sm text-gray-400 mb-4">Verfügbar ab:</div>
            <div className="flex items-center justify-center space-x-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{daysLeft}</div>
                <div className="text-sm text-gray-400">Tage</div>
              </div>
              <div className="text-2xl font-bold text-gray-500">:</div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{hoursLeft}</div>
                <div className="text-sm text-gray-400">Stunden</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Pricing Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-20">
          {pricingPlans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px 0px -50px 0px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`relative h-full ${plan.popular ? 'z-10' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-primary to-purple-600 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                  Beliebteste Wahl
                </div>
              )}
              <div className={`h-full bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border ${
                plan.popular 
                  ? 'border-primary/50 shadow-lg shadow-primary/10' 
                  : 'border-gray-700/50 hover:border-gray-600/50'
              } transition-all duration-300`}>
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-gray-400 mb-6">{plan.description}</p>
                
                <div className="mb-8">
                  <span className="text-4xl font-bold text-white">
                    {typeof plan.price === 'number' ? `€${plan.price}` : plan.price}
                  </span>
                  {typeof plan.price === 'number' && (
                    <span className="text-gray-400">/Einmalig</span>
                  )}
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <svg className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button 
                  className={`w-full py-3 px-6 rounded-xl font-medium transition-all duration-300 ${
                    plan.buttonVariant === 'primary'
                      ? 'bg-gradient-to-r from-primary to-purple-600 text-white hover:opacity-90 hover:shadow-lg hover:shadow-primary/30'
                      : 'bg-gray-700/50 text-white border border-gray-600 hover:border-primary/50 hover:bg-gray-700/80'
                  }`}
                >
                  {plan.buttonText}
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Contact Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50"
        >
          <h3 className="text-2xl font-bold text-white mb-2 text-center">Haben Sie Fragen?</h3>
          <p className="text-gray-400 text-center mb-8">Unser Team steht Ihnen gerne für weitere Informationen zur Verfügung.</p>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent"
                  placeholder="Ihr Name"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">E-Mail</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent"
                  placeholder="ihre@email.com"
                  required
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="plan" className="block text-sm font-medium text-gray-300 mb-2">Interessiert an</label>
              <select
                id="plan"
                name="plan"
                value={formData.plan}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent"
              >
                <option value="Starter">Starter</option>
                <option value="Professional">Professional</option>
                <option value="Enterprise">Enterprise</option>
                <option value="Other">Andere Frage</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">Nachricht</label>
              <textarea
                id="message"
                name="message"
                rows={4}
                value={formData.message}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent"
                placeholder="Ihre Nachricht oder Frage..."
              ></textarea>
            </div>
            
            <div className="flex items-center">
              <button
                type="submit"
                className="px-8 py-4 bg-gradient-to-r from-primary to-purple-600 text-white font-medium rounded-xl hover:opacity-90 transition-opacity flex items-center"
              >
                Nachricht senden
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
              
              <div className="ml-6 text-sm text-gray-400">
                <p>Oder rufen Sie uns an: <a href="tel:+49123456789" className="text-primary hover:underline">+49 123 456 789</a></p>
              </div>
            </div>
          </form>
        </motion.div>
        
        {/* Trust badges */}
        <div className="mt-16 pt-8 border-t border-gray-800">
          <p className="text-center text-gray-400 mb-8">Sichere Zahlung mit</p>
          <div className="flex flex-wrap items-center justify-center gap-8 opacity-70">
            {['Visa', 'Mastercard', 'PayPal', 'Klarna', 'ApplePay', 'GooglePay'].map((method, i) => (
              <div key={i} className="text-gray-400 text-lg font-medium">
                {method}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Preorder;
